import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Spin,
  message,
  Tag,
  Card,
  Alert,
  Tooltip,
  Divider,
  Statistic,
  Row,
  Col,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { transportApi } from "../../api/transportApi";
import { useFyStore } from "../../stores/fyStore";
import { StaticModal } from "../../shared/components/StaticModal";
import { StaticDrawer } from "../../shared/components/StaticDrawer";

interface DocumentSequence {
  id: number;
  financialYearId: number;
  financialYearCode: string;
  documentType: string;
  branchCode: string;
  prefix: string;
  currentNumber: number;
  padding: number;
  nextNumber: number;
  nextDocumentNumber: string;
  isActive: boolean;
  timesUsed: number;
  createdOn: string;
  createdBy: string;
  updatedOn?: string;
  updatedBy?: string;
}

const DOCUMENT_TYPES = ["Consignment", "Manifest", "GatePass", "Delivery"];

interface BranchOption {
  id: number;
  label: string;
  subtitle?: string;
}

const DocumentSequenceManagementTab: React.FC = () => {
  const { activeFyId } = useFyStore();
  const [sequences, setSequences] = useState<DocumentSequence[]>([]);
  const [loading, setLoading] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editDrawerVisible, setEditDrawerVisible] = useState(false);
  const [selectedSequence, setSelectedSequence] = useState<DocumentSequence | null>(null);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [branches, setBranches] = useState<BranchOption[]>([]);
  const [branchLoading, setBranchLoading] = useState(false);

  useEffect(() => {
    if (activeFyId) {
      loadSequences();
    }
  }, [activeFyId]);

  const loadSequences = async () => {
    if (!activeFyId) {
      message.warning("Please select an active financial year first");
      return;
    }

    setLoading(true);
    try {
      const response = await transportApi.listDocumentSequencesByFinancialYear(activeFyId);
      setSequences(response.data || []);
    } catch (err) {
      message.error("Failed to load document sequences");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadBranches = async (searchQuery = "") => {
    setBranchLoading(true);
    try {
      const response = await transportApi.searchBranchLookups(searchQuery, 1, 100);
      setBranches(response.items || []);
    } catch (err) {
      message.error("Failed to load branches");
      console.error(err);
    } finally {
      setBranchLoading(false);
    }
  };

  const handleBranchSearch = (value: string) => {
    loadBranches(value);
  };

  const handleCreateModalOpen = () => {
    setCreateModalVisible(true);
    loadBranches();
  };

  const handleCreateSubmit = async (values: any) => {
    setSubmitting(true);
    try {
      const payload = {
        documentType: values.documentType,
        branchCode: values.branchCode || "MAIN",
        prefix: values.prefix,
        padding: values.padding || 6,
      };

      await transportApi.createDocumentSequence(payload);
      message.success(`Document sequence created: ${payload.prefix}000001`);
      setCreateModalVisible(false);
      form.resetFields();
      loadSequences();
    } catch (err: any) {
      message.error(err.message || "Failed to create document sequence");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditSubmit = async (values: any) => {
    if (!selectedSequence) return;

    setSubmitting(true);
    try {
      const payload = {
        branchCode: selectedSequence.branchCode, // Cannot be changed
        prefix: values.prefix,
        padding: values.padding,
        currentNumber: values.currentNumber,
        isActive: values.isActive,
      };

      await transportApi.updateDocumentSequence(selectedSequence.id, payload);
      message.success("Document sequence updated");
      setEditDrawerVisible(false);
      setSelectedSequence(null);
      editForm.resetFields();
      loadSequences();
    } catch (err: any) {
      message.error(err.message || "Failed to update document sequence");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivate = (sequence: DocumentSequence) => {
    Modal.confirm({
      title: "Deactivate Document Sequence",
      content: `Are you sure you want to deactivate the sequence for ${sequence.documentType}? Make sure you have another active sequence for this document type.`,
      okText: "Yes, Deactivate",
      cancelText: "Cancel",
      okType: "danger",
      onOk: async () => {
        try {
          await transportApi.deactivateDocumentSequence(sequence.id);
          message.success("Document sequence deactivated");
          loadSequences();
        } catch (err: any) {
          message.error(err.message || "Failed to deactivate sequence");
        }
      },
    });
  };

  const handleEditClick = (sequence: DocumentSequence) => {
    setSelectedSequence(sequence);
    editForm.setFieldsValue({
      documentType: sequence.documentType,
      branchCode: sequence.branchCode,
      prefix: sequence.prefix,
      padding: sequence.padding,
      currentNumber: sequence.currentNumber,
      isActive: sequence.isActive,
    });
    setEditDrawerVisible(true);
  };

  const columns = [
    {
      title: "Financial Year",
      dataIndex: "financialYearCode",
      key: "financialYearCode",
      width: 110,
      render: (text: string) => <Tag color="purple">{text}</Tag>,
    },
    {
      title: "Document Type",
      dataIndex: "documentType",
      key: "documentType",
      width: 150,
      render: (text: string) => <strong>{text}</strong>,
    },
    {
      title: "Branch",
      dataIndex: "branchCode",
      key: "branchCode",
      width: 130,
      render: (code: string) => (
        <Tooltip placement="bottom" title={code === "MAIN" ? "Applies to all branches (fallback)" : code}>
          <Tag color={code === "MAIN" ? "blue" : "default"}>{code}</Tag>
        </Tooltip>
      ),
    },
    {
      title: "Prefix",
      dataIndex: "prefix",
      key: "prefix",
      width: 100,
      render: (text: string) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: "Next Document",
      dataIndex: "nextDocumentNumber",
      key: "nextDocumentNumber",
      width: 140,
      render: (text: string) => (
        <strong style={{ color: "#1890ff", fontSize: "14px" }}>{text}</strong>
      ),
    },
    {
      title: "Current #",
      dataIndex: "currentNumber",
      key: "currentNumber",
      width: 100,
      align: "center" as const,
    },
    {
      title: "Times Used",
      dataIndex: "timesUsed",
      key: "timesUsed",
      width: 100,
      align: "center" as const,
      render: (count: number) => <Tag color="green">{count}</Tag>,
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      width: 100,
      render: (isActive: boolean) => (
        <Tag color={isActive ? "success" : "error"}>
          {isActive ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      render: (_: any, record: DocumentSequence) => (
        <Space size="small">
          <Tooltip placement="bottom" title="Edit configuration">
            <Button
              type="primary"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEditClick(record)}
            >
              Edit
            </Button>
          </Tooltip>
          {record.isActive && (
            <Tooltip placement="bottom" title="Deactivate this sequence">
              <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => handleDeactivate(record)}
              >
                Deactivate
              </Button>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const activeSequences = sequences.filter((s) => s.isActive).length;
  const inactiveSequences = sequences.filter((s) => !s.isActive).length;

  return (
    <Spin spinning={loading}>
      <Card
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <strong>Document Sequence Configuration</strong>
            <InfoCircleOutlined
              style={{ color: "#8c8c8c", fontSize: "14px" }}
              title="Configure document numbering for consignments, manifests, and other documents"
            />
          </div>
        }
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreateModalOpen}
          >
            New Sequence
          </Button>
        }
        bordered={false}
        style={{ marginBottom: "20px" }}
      >
        <Alert
          message="Document Numbering"
          description="Document sequences ensure unique, sequential numbering for all transport documents. Each sequence is tied to a financial year and can be configured per branch."
          type="info"
          showIcon
          style={{ marginBottom: "20px" }}
        />

        <Row gutter={16} style={{ marginBottom: "20px" }}>
          <Col span={8}>
            <Statistic
              title="Active Sequences"
              value={activeSequences}
              prefix={<CheckCircleOutlined style={{ color: "#52c41a" }} />}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title="Inactive Sequences"
              value={inactiveSequences}
              prefix={<WarningOutlined style={{ color: "#faad14" }} />}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title="Total Sequences"
              value={sequences.length}
              prefix={<InfoCircleOutlined style={{ color: "#1890ff" }} />}
            />
          </Col>
        </Row>

        <Divider />

        <Table
          dataSource={sequences}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          size="small"
          columns={[
            {
              title: "SN",
              width: 70,
              render: (_: any, __: DocumentSequence, index: number) => index + 1,
            },
            ...columns,
          ]}
        />
      </Card>

      {/* Create Modal */}
      <StaticModal
        title="Create Document Sequence"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        confirmLoading={submitting}
        width={600}
        destroyOnHidden
        footer={[
          <Button key="cancel" onClick={() => {
            setCreateModalVisible(false);
            form.resetFields();
          }}>
            Close
          </Button>,
          <Button key="submit" type="primary" loading={submitting} onClick={() => form.submit()}>
            Create Sequence
          </Button>,
        ]}
      >
        <Form form={form} layout="vertical" onFinish={handleCreateSubmit}>
          <Alert
            message="Branch matching rules"
            description={
              <>
                <div>Sequences are matched by the exact branch code on the document (e.g. its From-Branch).</div>
                <div style={{ marginTop: 4 }}>Use <strong>MAIN</strong> as the branch to serve as a catch-all fallback for any branch that has no specific sequence configured.</div>
                <div style={{ marginTop: 4 }}>Once saved, document type and branch cannot be changed.</div>
              </>
            }
            type="warning"
            showIcon
            style={{ marginBottom: "16px" }}
          />

          <Form.Item
            label="Document Type"
            name="documentType"
            rules={[{ required: true, message: "Select document type" }]}
          >
            <Select placeholder="Select document type">
              {DOCUMENT_TYPES.map((type) => (
                <Select.Option key={type} value={type}>
                  {type}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Branch"
            name="branchCode"
            initialValue="MAIN"
            tooltip="Select a branch for this document sequence. Usually MAIN for organization-wide numbering."
            rules={[{ required: true, message: "Please select a branch" }]}
          >
            <Select
              placeholder="Search and select branch..."
              loading={branchLoading}
              onSearch={handleBranchSearch}
              filterOption={false}
              showSearch
            >
              {branches.map((branch) => {
                // subtitle format from API: "BRANCHCODE • City, State"
                const branchCode = branch.subtitle?.split("•")[0]?.trim() || branch.label;

                return (
                  <Select.Option key={branch.id} value={branchCode}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <Tag color="blue" style={{ minWidth: 50, textAlign: "center", margin: 0 }}>{branchCode}</Tag>
                      <span>{branch.label}</span>
                    </div>
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>

          <Form.Item
            label="Number Prefix"
            name="prefix"
            rules={[
              { required: true, message: "Prefix is required" },
              {
                pattern: /^[A-Z]{1,5}$/,
                message: "Prefix must be 1-5 uppercase letters",
              },
            ]}
            tooltip="e.g., CSG for Consignment, MFT for Manifest"
          >
            <Input
              placeholder="e.g., CSG"
              maxLength={5}
              style={{ textTransform: "uppercase" }}
            />
          </Form.Item>

          <Form.Item
            label="Padding (Number of Digits)"
            name="padding"
            initialValue={6}
            rules={[
              { required: true },
              {
                type: "number",
                min: 1,
                max: 10,
                message: "Padding must be between 1 and 10",
              },
            ]}
            tooltip="Number of digits to pad. E.g., 6 gives CSG000001"
          >
            <InputNumber min={1} max={10} />
          </Form.Item>

          <Form.Item noStyle>
            <div style={{ padding: "12px", backgroundColor: "#f0f2f5", borderRadius: "4px" }}>
              <strong>Preview:</strong> CSG000001, CSG000002, ... CSG999999
            </div>
          </Form.Item>
        </Form>
      </StaticModal>

      {/* Edit Drawer */}
      <StaticDrawer
        title={`Edit ${selectedSequence?.documentType} Sequence`}
        placement="right"
        onClose={() => {
          setEditDrawerVisible(false);
          setSelectedSequence(null);
          editForm.resetFields();
        }}
        open={editDrawerVisible}
        width={500}
        destroyOnClose
        footer={
          <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
            <Button onClick={() => {
              setEditDrawerVisible(false);
              setSelectedSequence(null);
              editForm.resetFields();
            }}>
              Close
            </Button>
            <Button type="primary" loading={submitting} onClick={() => editForm.submit()}>
              Save Changes
            </Button>
          </div>
        }
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleEditSubmit}
        >
          <Alert
            message="Immutable Fields"
            description="Document Type and Branch Code cannot be changed after creation."
            type="info"
            showIcon
            style={{ marginBottom: "16px" }}
          />

          <Form.Item label="Document Type" initialValue={selectedSequence?.documentType}>
            <Input disabled value={selectedSequence?.documentType} />
          </Form.Item>

          <Form.Item label="Branch Code" initialValue={selectedSequence?.branchCode}>
            <Input disabled value={selectedSequence?.branchCode} />
          </Form.Item>

          <Divider />

          <Form.Item
            label="Number Prefix"
            name="prefix"
            rules={[{ required: true }]}
            tooltip="Can be changed to modify the prefix for new documents"
          >
            <Input maxLength={5} style={{ textTransform: "uppercase" }} />
          </Form.Item>

          <Form.Item
            label="Padding"
            name="padding"
            rules={[{ type: "number", min: 1, max: 10 }]}
          >
            <InputNumber min={1} max={10} />
          </Form.Item>

          <Form.Item
            label="Current Number"
            name="currentNumber"
            tooltip="The next document will use CurrentNumber + 1"
            rules={[{ type: "number", min: 0 }]}
          >
            <InputNumber min={0} />
          </Form.Item>

          <Form.Item
            label="Active Status"
            name="isActive"
            valuePropName="checked"
          >
            <Input type="checkbox" />
          </Form.Item>
        </Form>
      </StaticDrawer>
    </Spin>
  );
};

export default DocumentSequenceManagementTab;
